import React, { useMemo, useState } from "react";
import { Button } from "antd";
import styles from "../../styles/Components/new-insight/InsightSignalsSection.module.css";
import InsightSignalsTable from "./signals-section/InsightSignalsTable";
import InsightSignalOperations from "./signals-section/InsightSignalOperations";
import CurrentStrategyInfo from "./signals-section/CurrentStrategyInfo";
import { useDispatch, useSelector } from "react-redux";
import TickerWidget from "../charts/TickerWidget";
import PageHeader from "../../ui/PageHeader";
import ReportBoard from "../../shared/ReportBoard";
import { isStockBoard, quoteCards, sortStockCards, stockBoardCards, stockBoardLabel, stockPivotTargets } from "../../shared/stockBoards";
import { loadExtraSymbols } from "../../shared/extraSymbols";
import AddBoardSymbol from "../../shared/AddBoardSymbol";
import TradeAssistantCard from "../trading-assistant/assistant/TradeAssistantCard";
import cardStyles from "../../styles/Components/trading-assistant/tradeAssistantCards.module.sass";
import { INSIGHT_QUESTIONS, INSIGHT_SCENARIOS, PRICE_EXAMPLES, openGuideChat } from "../../shared/GuideChat";
import { getInsightArtifacts } from "../../redux/new-insight/actions";
import {
  liveSuggestionFor,
  LIVE_TIMEFRAMES,
  normalizeLiveTimeframe,
  pivotsSettled,
} from "../../redux/live-suggestion/liveSuggestionSlice";
import useLiveSuggestionRefresh from "../../hooks/api/ai-assistant/useLiveSuggestionRefresh";
import usePowerfulSignalNotice from "../../hooks/usePowerfulSignalNotice";

function insightAction(value) {
  const action = String(value || "Hold").split("/")[0];
  return ["Buy", "Sell", "Hold"].includes(action) ? action : "Hold";
}

const STRENGTH_LABEL = {
  1: "Faint",
  2: "Feeble",
  3: "Fair",
  4: "Robust",
  5: "Powerful",
};

function strengthLabel(value) {
  const n = parseInt(value, 10);
  if (!Number.isFinite(n) || n <= 0) return "";
  const clamped = Math.max(1, Math.min(5, n));
  return STRENGTH_LABEL[clamped] || "";
}

function buildInsightVisualSummary(signalData) {
  const actions = { Buy: 0, Sell: 0, Hold: 0 };
  const categories = {};
  const strengths = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
  const rows = [];
  (signalData || []).forEach((item) => {
    const coin = item?.coin;
    Object.entries(item || {}).forEach(([name, payload]) => {
      if (name === "coin" || !payload || typeof payload !== "object" || !payload.suggestion) {
        return;
      }
      const suggestion = insightAction(payload.suggestion);
      const isGap = Boolean(payload.data_gap);
      const rawStrength = payload["signal strength"];
      const hasStrength = rawStrength !== undefined && rawStrength !== null && rawStrength !== "";
      if (!isGap) {
        actions[suggestion] += 1;
        const cat = payload.other_info?.category || "Unknown";
        categories[cat] = (categories[cat] || 0) + 1;
        if (hasStrength) {
          const st = Math.max(1, Math.min(5, parseInt(rawStrength, 10) || 1));
          strengths[st] = (strengths[st] || 0) + 1;
        }
      }
      if (suggestion === "Hold" && !isGap && !hasStrength) return;
      rows.push({
        coin,
        indicator: payload.other_info?.name || name,
        suggestion,
        strength: strengthLabel(rawStrength),
        roi: payload["recent ROI"],
        data_gap: isGap,
      });
    });
  });
  return { actions, categories, strengths, rows };
}

function liveForCoin(suggestions, coin, timeframe) {
  return liveSuggestionFor(suggestions, coin, timeframe);
}

function liveLabel(prediction, confidence) {
  const side = String(prediction || "").split("/")[0];
  if (!side) return "";
  if (side === "Hold") return "Hold";
  const n = Number(confidence);
  const pct = Number.isFinite(n) ? (n > 1 ? n : n * 100) : 0;
  return `${pct >= 75 ? "Strong" : "Weak"} ${side}`;
}

function buildInsightLiveSnapshot(signalData, suggestions, timeframe) {
  const liveTimeframe = normalizeLiveTimeframe(timeframe);
  return (signalData || [])
    .filter((item) => item?.coin)
    .map((item) => {
      const live = liveForCoin(suggestions, item.coin, liveTimeframe);
      const liveSide = live?.prediction
        ? String(live.prediction).split("/")[0]
        : "";
      const livePresent = Boolean(live && live.prediction);
      return {
        symbol: item.coin,
        live_side: liveSide,
        live_confidence: live?.confidence ?? null,
        live_label: livePresent ? liveLabel(liveSide, live.confidence) : "",
        pivot_detected: livePresent && (liveSide === "Buy" || liveSide === "Sell"),
        pivot_timeframe: livePresent ? liveTimeframe : "",
      };
    });
}

function overlayInsightLive(visual, snapshot, timeframe) {
  const byCoin = {};
  (snapshot || []).forEach((item) => {
    const key = String(item.symbol || "")
      .split("/")[0]
      .toUpperCase();
    if (key) byCoin[key] = item;
  });
  const pivots = [];
  const live_conflicts = [];
  const rows = (visual?.rows || []).map((row) => {
    const snap = byCoin[String(row.coin || "").split("/")[0].toUpperCase()];
    if (!snap) return row;
    const next = { ...row };
    if (snap.live_side) {
      next.live_side = snap.live_side;
      next.live_label = snap.live_label;
      next.live_confidence = snap.live_confidence;
    }
    if (snap.pivot_detected) {
      next.pivot = "on";
      pivots.push({
        symbol: row.coin,
        timeframe: snap.pivot_timeframe || "15m",
        live_side: snap.live_side,
      });
    }
    const board = String(row.suggestion || "").split("/")[0];
    if (
      snap.live_side &&
      (snap.live_side === "Buy" || snap.live_side === "Sell") &&
      board &&
      snap.live_side !== board
    ) {
      next.conflict = true;
      live_conflicts.push(
        `${row.coin} board ${board} vs ${snap.pivot_timeframe || "15m"} ${snap.live_side}`,
      );
    }
    return next;
  });
  (snapshot || []).forEach((item) => {
    const key = String(item.symbol || "")
      .split("/")[0]
      .toUpperCase();
    if (!key || rows.some((row) => String(row.coin || "").split("/")[0].toUpperCase() === key)) {
      return;
    }
    if (
      item.live_side !== "Buy" &&
      item.live_side !== "Sell" &&
      !item.pivot_detected
    ) {
      return;
    }
    rows.push({
      coin: item.symbol,
      indicator: `${item.pivot_timeframe || "15m"} live`,
      suggestion: "Hold",
      live_side: item.live_side,
      live_label: item.live_label,
      live_confidence: item.live_confidence,
      pivot: item.pivot_detected ? "on" : "off",
    });
    if (item.pivot_detected) {
      pivots.push({
        symbol: item.symbol,
        timeframe: item.pivot_timeframe || "15m",
        live_side: item.live_side,
      });
    }
  });
  const next = { ...visual, rows, pivots, live_conflicts };
  if (LIVE_TIMEFRAMES.includes(timeframe)) {
    next.live_timeframe = timeframe;
  }
  return next;
}

function InsightSignalsSection() {
  const dispatch = useDispatch();
  const {
    main: { userData },
    liveSuggestion: { suggestions, liveTimeframe, coinStatus, macroRegime },
    newInsightState: {
      signalData,
      insightSummary,
      exportLinks,
      chartId,
      signalStatus,
      insightArtifactsStatus,
      insightMarketType,
    },
  } = useSelector((state) => state);

  const selectedLiveTimeframe = normalizeLiveTimeframe(liveTimeframe);
  const [extraTick, setExtraTick] = useState(0);
  const extraSymbols = useMemo(
    () => loadExtraSymbols(insightMarketType),
    [insightMarketType, extraTick],
  );
  const stockBoard = isStockBoard(insightMarketType);
  const stockRows = stockBoard ? stockBoardCards(insightMarketType, extraSymbols) : [];
  const forexExtras = insightMarketType === "forex" ? quoteCards(extraSymbols) : [];
  const pivotTargets = useMemo(() => {
    if (stockBoard) return stockPivotTargets(insightMarketType, extraSymbols);
    if (insightMarketType === "forex") {
      return extraSymbols.map((symbol) => ({
        symbol,
        exchange: "yahoo",
        timeframe: "1d",
        stock: true,
      }));
    }
    if ((insightMarketType || "crypto") !== "crypto") return [];
    const seen = new Set();
    const targets = [];
    (signalData || []).forEach((item) => {
      const symbol = item?.coin;
      if (!symbol || seen.has(symbol)) return;
      seen.add(symbol);
      targets.push({ symbol, exchange: "kucoin" });
    });
    return targets;
  }, [insightMarketType, signalData, stockBoard, extraSymbols]);
  const pivotsReady = pivotsSettled(
    pivotTargets,
    suggestions,
    coinStatus,
    selectedLiveTimeframe,
  );
  useLiveSuggestionRefresh(
    (insightMarketType || "crypto") === "crypto" || stockBoard || insightMarketType === "forex",
    pivotTargets,
  );

  usePowerfulSignalNotice({
    kind: "insight",
    page: "Insights",
    boardKey: stockBoard ? insightMarketType : chartId,
    rows: stockBoard ? stockRows : signalData,
    suggestions,
    timeframe: stockBoard ? "1d" : selectedLiveTimeframe,
  });
  const liveSnapshot = buildInsightLiveSnapshot(
    signalData,
    suggestions,
    selectedLiveTimeframe,
  );
  const localVisual = overlayInsightLive(
    buildInsightVisualSummary(signalData),
    liveSnapshot,
    (insightMarketType || "crypto") === "crypto" ? selectedLiveTimeframe : "",
  );
  const visualSummary = { ...localVisual, macro_regime: macroRegime };

  const handleRequestArtifacts = () => {
    if (
      !chartId ||
      signalStatus !== "idle" ||
      insightArtifactsStatus === "loading" ||
      !pivotsReady
    ) {
      return;
    }

    dispatch(
      getInsightArtifacts(
        userData?.["custom:custom_username"],
        userData?.email,
        chartId,
        liveSnapshot,
      ),
    );
  };

  return (
    <div className={styles.container}>
      <PageHeader
        title="Strategy Builder"
        service="insight"
        extra={
          <Button type="primary" onClick={openGuideChat}>
            Ask AI
          </Button>
        }
      />

      <InsightSignalOperations />

      {stockBoard ? null : <CurrentStrategyInfo />}

      <ReportBoard
        variant="insight"
        summary={insightSummary}
        exportLinks={exportLinks}
        visualSummary={visualSummary}
        onRequest={handleRequestArtifacts}
        loading={insightArtifactsStatus === "loading"}
        error={insightArtifactsStatus === "error"}
        disabled={!chartId || signalStatus !== "idle" || !pivotsReady}
        statusNote={pivotsReady ? "" : "Waiting for pivots…"}
        actionLabel={pivotsReady ? undefined : "Waiting for pivots…"}
        leading={
          <div className={styles.ask}>
            <p>Ask AI</p>
            {INSIGHT_SCENARIOS.map((item) => (
              <button
                key={item.ask}
                type="button"
                className={styles.curious}
                title={item.ask}
                onClick={() => openGuideChat(item.ask)}
              >
                {item.icon ? <item.icon /> : null}
                {item.label}
              </button>
            ))}
            {INSIGHT_QUESTIONS.map((item) => (
              <button
                key={item.ask}
                type="button"
                className={styles.curious}
                title={item.ask}
                onClick={() => openGuideChat(item.ask)}
              >
                {item.icon ? <item.icon /> : null}
                {item.label}
              </button>
            ))}
          </div>
        }
        more={
          <div className={styles.ask}>
            <p>Price and daily examples</p>
            {PRICE_EXAMPLES.map((item) => (
              <button
                key={item.ask}
                type="button"
                title={item.ask}
                onClick={() => openGuideChat(item.ask)}
              >
                {item.icon ? <item.icon /> : null}
                {item.label}
              </button>
            ))}
          </div>
        }
      />

      {signalData.length > 0 && (
        <TickerWidget symbolsData={signalData.map((s) => s.coin)} />
      )}

      {stockBoard ? (
        <div className={cardStyles.cardsGrid}>
          <p>
            {stockBoardLabel(insightMarketType)} daily pivot. No category call on this board.
          </p>
          {sortStockCards(stockRows, suggestions).map((item) => (
            <TradeAssistantCard data={item} key={item.SYMBOL_NAME} />
          ))}
          <AddBoardSymbol
            market={insightMarketType}
            existing={stockRows.map((row) => row.SYMBOL_NAME)}
            onChange={() => setExtraTick((tick) => tick + 1)}
          />
        </div>
      ) : (
        <>
          {insightMarketType === "forex" ? (
            <div className={cardStyles.cardsGrid}>
              {forexExtras.map((item) => (
                <TradeAssistantCard data={item} key={item.SYMBOL_NAME} />
              ))}
              <AddBoardSymbol
                market="forex"
                onChange={() => setExtraTick((tick) => tick + 1)}
              />
            </div>
          ) : null}
          <InsightSignalsTable />
        </>
      )}
    </div>
  );
}

export default InsightSignalsSection;
